import { Formik, type FormikProps } from 'formik';
import * as React from 'react';
import { useEffect } from 'react';
import { DraftFormRender } from '../../components/PostDraft/DraftFormRender';
import type { SetFieldValue } from '../../constants/textEditor.constants';
import { NewPostInputInitialState } from '../../initial-states/newPostInput.initialState';
import {
  blockInitialValues,
  createNewBlock,
} from '../../initial-states/textBlocks.initialState';
import type { TextBlock } from '../../interfaces/textEditor.interface';
import { createPostSchema } from '../../yupValidationSchemas/createPostSchema';
import { handleBlockTypeChange } from './handlers/handleBlockTypeChange';
import { formatBlockForGraphQL } from './helpers/formatBlockForGraphQL';

const blockTypes = Object.keys(blockInitialValues);

export interface RichEditorBodyProps {
  username: string;
  userId: string;
  initialBlocks: TextBlock[];
  fileInputRef: React.MutableRefObject<null>;
  menuAnchors: { [key: string]: HTMLElement | null };
  headingMenuAnchors: { [key: string]: HTMLElement | null };
  handleMenuClick: (
    event: React.MouseEvent<HTMLButtonElement>,
    blockId: string,
  ) => void;
  handleOpenHeadingMenu: (
    event: React.MouseEvent<HTMLButtonElement>,
    blockId: string,
  ) => void;
  handleCloseHeadingMenu: (blockId: string) => void;
  handleHeadingLevelChange: (
    index: number,
    level: number,
    setFieldValue: SetFieldValue,
  ) => void;
  handleMenuClose: (blockId: string) => void;
  handleSave: (values: any) => void;
  hideSubmitButton?: boolean;
  formikRef?: React.RefObject<FormikProps<any> | null>;
}

export function RichEditorBody(props: RichEditorBodyProps): React.ReactElement {
  const {
    username,
    userId,
    initialBlocks,
    fileInputRef,
    menuAnchors,
    headingMenuAnchors,
    handleMenuClick,
    handleOpenHeadingMenu,
    handleCloseHeadingMenu,
    handleHeadingLevelChange,
    handleMenuClose,
    handleSave,
    hideSubmitButton,
    formikRef,
  } = props;

  return (
    <div className="APP__Simple__Dialog__Body">
      <Formik
        innerRef={formikRef}
        initialValues={{
          ...NewPostInputInitialState,
          blocks:
            initialBlocks.length > 0
              ? initialBlocks
              : [createNewBlock('Paragraph')],
        }}
        validationSchema={createPostSchema}
        onSubmit={(values, actions) => {
          // TODO: Remove this payload or,
          //  move the handleSave to the onSubmit
          const payload = {
            userId: userId,
            article: true,
            username,
            textBlocks: values.blocks
              .filter((block: TextBlock) => {
                if (block.type === 'Paragraph') {
                  return (
                    block.data?.paragraph != null &&
                    block.data.paragraph.trim() !== ''
                  );
                }
                if (block.type === 'Heading') {
                  return (
                    block.data?.header != null &&
                    block.data.header.trim() !== ''
                  );
                }
                // Add similar conditions for other block types
                return true;
              })
              .map(formatBlockForGraphQL),
          };
          setTimeout(() => {
            // alert(JSON.stringify(values, null, 2));
            actions.setSubmitting(false);
          }, 1000);
          actions.resetForm();
        }}
      >
        {({
          values,
          setFieldValue,
          handleSubmit,
          errors,
          touched,
          isSubmitting,
        }: FormikProps<any>) => {
          useEffect(() => {
            if (values.blocks?.length === 0) {
              setFieldValue('blocks', [createNewBlock('Paragraph')]);
            }
          }, [values.blocks, setFieldValue]);

          const addDraftPayload = {
            username,
            userId: userId,
            article: true,
            textBlocks: [...(values.blocks || [])],
          };

          return (
            <DraftFormRender
              blockTypes={blockTypes}
              errors={errors}
              fileInputRef={fileInputRef}
              handleBlockTypeChange={handleBlockTypeChange as any}
              handleCloseHeadingMenu={handleCloseHeadingMenu}
              handleHeadingLevelChange={handleHeadingLevelChange}
              handleMenuClick={handleMenuClick}
              handleMenuClose={handleMenuClose}
              handleOpenHeadingMenu={handleOpenHeadingMenu}
              handleSave={handleSave}
              hideSubmitButton={hideSubmitButton}
              handleSubmit={handleSubmit}
              headingMenuAnchors={headingMenuAnchors}
              isSubmitting={false}
              menuAnchors={menuAnchors}
              payload={addDraftPayload}
              setFieldValue={setFieldValue}
              touched={touched}
              values={values.blocks || initialBlocks}
            />
          );
        }}
      </Formik>
    </div>
  );
}
